/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";
import type { useTranslations } from "@i18n/react";
import type { AnalyticsContext } from "@packages/analytics";
import { HallowAnalyticsEvent } from "@packages/analytics";
import { Link } from "@packages/framework";
import type {
  AssignmentSectionItem,
  TheWordSectionItem,
  TriviaSectionItem,
} from "@packages/sdk";
import { getURLFromDeeplink, useBranch } from "@packages/sdk";
import type { format } from "date-fns/format";
import type { FC, PropsWithChildren } from "react";
import { Fragment } from "react";

import { spacing } from "../../../../../../global/emotion";
import type { CarouselApi } from "../../../../../hooks/useCarousel";
import { useModal } from "../../../../../niceModal";
import type { KeyedElement } from "../../../../../types/keyedElement.types";
import type { GridProps } from "../../../../blocks/layout/Grid/Grid.emotion";
import { Card } from "../../../../refactor/Card/Card.emotion";
import { Button } from "../../../_core/Button/Button.emotion";
import { IconButton } from "../../../_core/IconButton/IconButton.emotion";
import {
  Edit2Icon,
  InfoIcon,
  PinIcon,
  PlusIcon,
  PrayIcon,
  ReflectionIcon,
  ShareIcon,
  StarIcon,
  Support1Icon,
  TimeIcon,
} from "../../../icons/Icon/GeneralIcons";
import {
  ChevronRightIcon,
  MoreIcon,
} from "../../../icons/Icon/NavigationIcons";
import { ShareModal } from "../../../modals/ShareModal/ShareModal.emotion";
import { CampaignLargeWithAuthor } from "../../campaigns/CampaignLargeWithAuthor/CampaignLargeWithAuthor.emotion";
import { CampaignMedium } from "../../campaigns/CampaignMedium/CampaignMedium.emotion";
import { CategoryLarge } from "../../categories/CategoryLarge/CategoryLarge.emotion";
import {
  CategoryMedium,
  type CategoryMediumProps,
} from "../../categories/CategoryMedium/CategoryMedium.emotion";
import { ChallengeLarge } from "../../challenges/ChallengeLarge/ChallengeLarge.emotion";
import {
  ContentLarge,
  type ContentLargeProps,
} from "../../content/ContentLarge/ContentLarge.emotion";
import {
  ContentMedium,
  type ContentMediumProps,
} from "../../content/ContentMedium/ContentMedium.emotion";
import {
  ContentSmall,
  type ContentSmallProps,
} from "../../content/ContentSmall/ContentSmall.emotion";
import { Assignment } from "../../other/Assignment.emotion";
import { DailyQuote } from "../../other/DailyQuote/DailyQuote.emotion";
import { TextFirstSectionItem } from "../../other/TextFirstSectionItem.emotion";
import { TheWordLarge } from "../../the-word/TheWordLarge.emotion";
import { TheWordMedium } from "../../the-word/TheWordMedium.emotion";
import { TriviaLarge } from "../../trivia/TriviaLarge.emotion";
import { TriviaMedium } from "../../trivia/TriviaMedium.emotion";
import { getSectionItemAssignmentProps } from "./getAssignmentSectionItemProps";
import {
  getSectionItemCampaignMediumProps,
  getSectionItemCampaignProps,
} from "./getCampaignSectionItemProps";
import { getSectionItemCategoryProps } from "./getCategorySectionItemProps";
import { getSectionItemChallengeProps } from "./getChallengeSectionItemProps";
import { getSectionItemContentProps } from "./getContentSectionItemProps";
import { getSectionItemDailyQuoteProps } from "./getDailyQuoteSectionItemProps";
import {
  getSectionItemTheWordProps,
  getSmallSectionItemTheWordProps,
} from "./getTheWordSectionItemProps";
import {
  getSectionItemTriviaProps,
  getSmallSectionItemTriviaProps,
} from "./getTriviaSectionItemProps";
import type { SectionProps } from "./Section.emotion";

export type GetSectionItemsProps = Pick<
  SectionProps,
  | "itemTypeLinkSearchParams"
  | "link"
  | "mutate"
  | "section"
  | "pageId"
  | "screenName"
  | "showUnsupportedSections"
> & {
  analytics?: AnalyticsContext;
  t: ReturnType<typeof useTranslations>;
  isDesktop: boolean;
  format?: typeof format;
};

const titleLinkStyle: Interpolation = {
  marginLeft: spacing.negativeM,
};

export const getSectionItems = ({
  analytics,
  itemTypeLinkSearchParams,
  link,
  mutate,
  section,
  pageId,
  screenName,
  t,
  isDesktop,
  format,
  showUnsupportedSections,
}: GetSectionItemsProps) => {
  let items: KeyedElement[] | null;
  const rightActions: KeyedElement[] = [];
  let size: CarouselApi["size"] | GridProps["size"] = "m";
  let TitleLink: FC<PropsWithChildren> = Fragment;

  const shareModal = useModal(ShareModal);
  const branch = useBranch();

  if (section.id === 1310) return { items: [], rightActions, size, TitleLink };

  const supportedSectionItems = showUnsupportedSections
    ? section.items
    : section.items.filter(
        (item) => item.reference_type !== "community_challenge",
      );

  if (supportedSectionItems.length) {
    switch (section.item_type) {
      case "campaign":
        items = supportedSectionItems
          .map((item) =>
            item.item_type !== section.item_type ? null : (
              <CampaignLargeWithAuthor
                {...getSectionItemCampaignProps({
                  analytics,
                  item,
                  link,
                  section,
                  pageId,
                  screenName,
                  t,
                })}
                data-id={item.id}
                key={`${section.id}_${item.id}`}
              />
            ),
          )
          .filter((section) => !!section);
        size = "xl";
        break;
      case "daily_quote":
        items = supportedSectionItems.map((item) => (
          <DailyQuote
            data-id={item.id}
            key={`${section.id}_${item.id}`}
            {...getSectionItemDailyQuoteProps({
              item,
              t,
              onShare: async () =>
                item?.reference?.shareable_url
                  ? shareModal.show({
                      title: item?.reference?.text,
                      description: item?.reference?.attributed_to,
                      link: await branch.dailyQuote({
                        id: item.reference_id,
                        url: item.reference?.shareable_url,
                      }),
                      imgSrc: item?.reference?.image_url,
                      color: "#000",
                    })
                  : null,
            })}
          />
        ));
        size = "xl";
        break;
      case "deeplink_card":
      case "deeplink_card_medium":
      case "deeplink_card_small":
        let CategoryComponent: typeof CategoryLarge | typeof CategoryMedium;

        if (section.item_type === "deeplink_card") {
          CategoryComponent = CategoryLarge;
          size = "m";
        } else {
          CategoryComponent = CategoryMedium;
          size = "s";
        }

        items = supportedSectionItems.map((item) => (
          <CategoryComponent
            data-id={item.id}
            key={`${section.id}_${item.id}`}
            {...(getSectionItemCategoryProps({
              analytics,
              section,
              item,
              link,
              pageId,
              size: section.item_type === "deeplink_card" ? "l" : "m",
              screenName,
              t,
            }) as CategoryMediumProps)}
          />
        ));
        break;
      case "large":
        items = supportedSectionItems.map((item) => {
          if (
            item.reference_type === "challenge" ||
            item.reference_type === "collection_and_session" ||
            (item.reference_type === "collection" &&
              item.reference?.type === "challenge")
          )
            return (
              <ChallengeLarge
                data-id={item.id}
                key={`${section.id}_${item.id}`}
                {...getSectionItemChallengeProps({
                  item,
                  link,
                  t,
                  mutate,
                  analytics,
                  screenName,
                })}
              />
            );

          return (
            <ContentLarge
              data-id={item.id}
              key={`${section.id}_${item.id}`}
              {...(getSectionItemContentProps({
                analytics,
                item,
                link,
                size: "l",
                pageId,
                screenName,
                t,
              }) as ContentLargeProps)}
            />
          );
        });
        size = "xl";
        break;
      case "medium":
        items = supportedSectionItems.map((item) => {
          if (item.reference_type === "the_word")
            return (
              <ContentMedium
                {...getSmallSectionItemTheWordProps({
                  item,
                  link,
                  screenName,
                  t,
                  analytics,
                })}
              />
            );

          if (item.reference_type === "trivia")
            return (
              <ContentMedium
                {...getSmallSectionItemTriviaProps({
                  item,
                  link,
                  t,
                  screenName,
                  analytics,
                  format,
                })}
              />
            );

          if (item.reference_type === "trivia_leaderboard")
            return (
              <ContentMedium
                {...getSmallSectionItemTriviaProps({
                  item: {
                    ...item,
                    type: "trivia",
                    reference_type: "trivia",
                    reference: item.reference.game,
                  },
                  link,
                  screenName,
                  t,
                  analytics,
                  format,
                })}
              />
            );

          if (item.reference_type === "campaign")
            return (
              <CampaignMedium
                data-id={item.id}
                key={`${section.id}_${item.id}`}
                {...getSectionItemCampaignMediumProps({
                  item,
                  link,
                  t,
                  screenName,
                })}
              />
            );
          return (
            <ContentMedium
              data-id={item.id}
              key={`${section.id}_${item.id}`}
              {...(getSectionItemContentProps({
                analytics,
                item,
                itemTypeLinkSearchParams,
                link,
                size: "m",
                pageId,
                screenName,
                t,
              }) as ContentMediumProps)}
            />
          );
        });
        size = "s";
        break;
      case "player_card":
        items = supportedSectionItems.map((item) =>
          item.reference_type === "challenge" ||
          item.reference_type === "collection_and_session" ||
          (item.reference_type === "collection" &&
            item.reference?.type === "challenge") ? (
            <ChallengeLarge
              data-id={item.id}
              key={`${section.id}_${item.id}`}
              {...getSectionItemChallengeProps({
                item,
                link,
                t,
                mutate,
                analytics,
                screenName,
              })}
            />
          ) : null,
        );
        size = "xl";
        break;
      case "small":
        items = supportedSectionItems.map((item) => (
          <ContentSmall
            data-id={item.id}
            key={`${section.id}_${item.id}`}
            {...(getSectionItemContentProps({
              analytics,
              item,
              link,
              size: "s",
              pageId,
              screenName,
              t,
            }) as ContentSmallProps)}
          />
        ));
        size = "l";
        break;
      case "trivia":
        items = supportedSectionItems.map((item) => (
          <TriviaLarge
            data-id={item.id}
            key={`${section.id}_${item.id}`}
            {...getSectionItemTriviaProps({
              item: item as TriviaSectionItem,
              link,
              screenName,
              t,
              analytics,
            })}
          />
        ));
        size = "xl";
        break;
      case "trivia_leaderboard":
        items = supportedSectionItems.map((item) => (
          <TriviaLarge
            data-id={item.id}
            key={`${section.id}_${item.id}`}
            {...getSectionItemTriviaProps({
              item: {
                ...item,
                type: "trivia",
                reference_type: "trivia",
                reference: item.reference?.game,
              } as TriviaSectionItem,
              link,
              screenName,
              t,
              analytics,
            })}
          />
        ));
        size = "xl";
        break;
      case "text_first_content":
        items = supportedSectionItems.map((item) =>
          item.reference_type === "text_first_content" ? (
            <TextFirstSectionItem
              key={`${section.id}_${item.id}`}
              {...item.reference}
            />
          ) : null,
        );
        size = "m";
        break;
      case "large_with_header":
      case "medium_with_header":
      case "assignment":
        items = supportedSectionItems.map((item) => (
          <Assignment
            key={`${section.id}_${item.id}`}
            {...getSectionItemAssignmentProps({
              item: item as AssignmentSectionItem,
              t,
              analytics,
              section,
              pageId,
              screenName,
              itemTypeLinkSearchParams,
              format,
            })}
          />
        ));
        size = isDesktop
          ? section.item_type === "large_with_header"
            ? "m"
            : "s"
          : "l";
        break;
      case "the_word":
        items = supportedSectionItems.map((item) => (
          <TheWordLarge
            key={`${section.id}_${item.id}`}
            {...getSectionItemTheWordProps({
              item: item as TheWordSectionItem,
              link,
              screenName,
              t,
              analytics,
            })}
          />
        ));
        size = "xl";
        break;
      case "games":
        items = supportedSectionItems.map((item) =>
          item.reference_type === "the_word" ? (
            <TheWordMedium
              key={`${section.id}_${item.id}`}
              {...getSectionItemTheWordProps({
                item: item as TheWordSectionItem,
                link,
                screenName,
                t,
                analytics,
              })}
            />
          ) : item.reference_type === "trivia" ? (
            <TriviaMedium
              key={`${section.id}_${item.id}`}
              {...getSectionItemTriviaProps({
                item,
                link,
                t,
                screenName,
                analytics,
              })}
            />
          ) : item.reference_type === "trivia_leaderboard" ? (
            <TriviaMedium
              key={`${section.id}_${item.id}`}
              {...getSectionItemTriviaProps({
                item: {
                  ...item,
                  type: "trivia",
                  reference_type: "trivia",
                  reference: item.reference?.game,
                } as TriviaSectionItem,
                link,
                screenName,
                t,
                analytics,
              })}
            />
          ) : null,
        );
        break;
      case "create_campaign_prompt":
      case "create_campaign_prompt_v2_final":
      case "magisterium_prompt":
      case "streak":
      case "text_summary":
      case "deeplink_card_condensed":
      default:
        if (showUnsupportedSections) {
          items = supportedSectionItems.map((item) => (
            <Card
              key={`${section.id}_${item.id}`}
              imgSrc={item.reference?.image_url}
              title={
                item.reference?.title ??
                `Unsupported ${item.item_type} section item`
              }
              description={
                <div>
                  <p>
                    {item.reference?.description ??
                      item.reference?.detailed_desc ??
                      item.reference?.short_desc ??
                      item.reference?.label_desc}
                  </p>
                  <p>This item is not supported on web.</p>
                </div>
              }
              data-id={item.id}
            />
          ));
        }
        break;
    }
  }

  if (section.action_link) {
    const onClickCapture = () => {
      analytics?.track({
        event: HallowAnalyticsEvent.TappedSectionActionButton,
        properties: {
          page_id: pageId,
          section_id: section.id,
          section_title: section.title,
          section_type: section.type,
          text: section.action_title,
          icon: section.action_icon,
          link: section.action_link,
        },
      });
    };
    const url = getURLFromDeeplink({ deeplink: section.action_link });
    if (section.action_icon) {
      let icon: KeyedElement | null = null;
      switch (section.action_icon) {
        case "plus":
          icon = <PlusIcon.Simple />;
          break;
        case "plus_outline":
          icon = <PlusIcon.Outline />;
          break;
        case "plus_filled":
          icon = <PlusIcon.Filled />;
          break;
        case "edit":
          icon = <Edit2Icon />;
          break;
        case "more":
          icon = <MoreIcon />;
          break;
        case "info":
          icon = <InfoIcon />;
          break;
        case "support":
          icon = <Support1Icon />;
          break;
        case "share":
          icon = <ShareIcon />;
          break;
        case "pin_outline":
          icon = <PinIcon.Outline />;
          break;
        case "pin_filled":
          icon = <PinIcon.Filled />;
          break;
        case "favorite_outline":
          icon = <StarIcon.Outline />;
          break;
        case "favorite_filled":
          icon = <StarIcon.Filled />;
          break;
        case "favorite_off":
          icon = <StarIcon.Off />;
          break;
        case "time":
          icon = <TimeIcon />;
          break;
        case "pray_outline":
          icon = <PrayIcon.Outline />;
          break;
        case "pray_filled":
          icon = <PrayIcon.Filled />;
          break;
        case "reflection":
          icon = <ReflectionIcon />;
          break;
        case "download":
        // we don't support downloading
        case "routine":
        // we don't support routines right now and it looks like we have the wrong
        // icon in our library right now, too, so just ignoring this for now
        default:
      }
      if (icon) {
        rightActions.push(
          <IconButton
            key="see_all"
            asChild
            onClickCapture={onClickCapture}
            size="s"
            variant="outline"
            icon={icon}
            title={section.action_title}
          >
            <Link href={url} />
          </IconButton>,
        );
      }
    } else {
      rightActions.push(
        <Button
          key="see_all"
          asChild
          onClickCapture={onClickCapture}
          size="s"
          variant="outline"
        >
          <Link href={url}>{section.action_title}</Link>
        </Button>,
      );
    }
  }

  if (section.title_link && !section.title_link.includes("leaderboard")) {
    TitleLink = ({ children }: PropsWithChildren) => (
      <Button
        asChild
        variant={"transparentPrimary"}
        endIcon={<ChevronRightIcon />}
        css={titleLinkStyle}
        onClickCapture={() =>
          analytics.track({
            event: HallowAnalyticsEvent.TappedSectionSeeAll,
            properties: {
              page_id: pageId,
              section_id: section.id,
              section_title: section.title,
              section_type: section.type,
              accessibility_text: section.title_link_accessibility,
              link: section.title_link,
            },
          })
        }
      >
        <Link
          href={getURLFromDeeplink({ deeplink: section.title_link })}
          title={section.title_link_accessibility}
        >
          {children}
        </Link>
      </Button>
    );
    TitleLink.displayName = "SectionTitleLink";
  }

  return { items, rightActions, size, TitleLink };
};
